import React, { useEffect, useState } from "react"
import { Button } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useField, useFormikContext } from "formik"
import { handleAutoCompleteMode, useAutocompleteController } from "../../form/useAutocompleteController"
import { SelectedMainUserState, BillReceiversItemMS } from "../../../../createOrder/createOrderTypes"
import { ConfigQuery, fetchBillReceiversMS, BillReceiversResponse } from "../../../createOrderApi"
import { ApiQueryAutocomplete } from "../../../../../../core/api/components/ApiQueryAutocomplete"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { DynamicConfigField, MeetingServiceTabValues } from "../../form/formTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useFormResetWatcher } from "../../form/useFormResetWatcher"
import { BillReceiverOptionItem } from "./BillReceiverOptionItem"
import { CreateBillReceiver } from "./CreateBillReceiver"
import { getFormikFieldError } from "../../form/helpers"

const useStyles = makeStyles()((theme) => ({
    buttonWrap: {
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        background: theme.palette.tailwind.sky[50],
    },
    button: {
        justifyContent: "start",
        color: theme.palette.tailwind.slate[950],
        fontSize: "1rem",
        textTransform: "none",
        transition: "0.25s ease-in-out",
        width: "100%",
        padding: ".5rem 1rem",
    },
    altGrey: {
        backgroundColor: theme.palette.tailwind.slate[50],
    },
    altWhite: {
        backgroundColor: theme.palette.common.white,
    },
}))
type Props = Readonly<{
    config: DynamicConfigField
    user: SelectedMainUserState
    showCreateButton?: boolean
}>

function selectBillReceivers(data: BillReceiversResponse): BillReceiversItemMS[] {
    return data?.billReceivers ?? []
}

export function BuyerPartiesAutocomplete({ config, user, showCreateButton }: Props) {
    const { classes } = useStyles()
    const [isOpenCreateBill, setIsOpenCreateBill] = useState(false)
    const [field, meta, helpers] = useField(config.key)
    const { submitCount } = useFormikContext<MeetingServiceTabValues>()
    const formikErrorProps = getFormikFieldError(meta, submitCount)
    const wasReset = useFormResetWatcher(config.key)

    const mode = handleAutoCompleteMode(config.inputField?.mode)
    const {
        debouncedSearch,
        enabled,
        isEagerLoad,
        isLoadOnOpen,
        forceRefreshKey,
        handleInputChange,
        handleOpen,
        handleClose,
        handleClearSearch,
    } = useAutocompleteController(mode, wasReset)

    const {
        data: options = [],
        isError,
        isLoading,
    } = useApiQuery(fetchBillReceiversMS, {
        queryName: ConfigQuery.MEETING_SERVICE_BILL_RECEIVERS,
        dependencies: {
            userId: user.userId,
            search: debouncedSearch,
            forceRefreshKey,
        },
        select: selectBillReceivers,
        enabled,
    })

    useEffect(() => {
        if (isEagerLoad && options.length === 1 && !field.value) {
            helpers.setValue(options[0], false)
        }
    }, [isEagerLoad, options, field.value, helpers])

    const ListboxWithHeader = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>(function ListboxWithHeader(
        props,
        ref,
    ) {
        const { children, ...other } = props
        return (
            <ul ref={ref} {...other}>
                {showCreateButton && (
                    <li role="presentation" onMouseDown={(e) => e.preventDefault()} className={classes.buttonWrap}>
                        <LocalizedStrict id="create-order-bill-receiver-add-button">
                            <Button
                                type="button"
                                variant="text"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setIsOpenCreateBill(true)
                                }}
                                className={classes.button}
                            >
                                + Add bill receiver
                            </Button>
                        </LocalizedStrict>
                    </li>
                )}
                {children}
            </ul>
        )
    })

    const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: BillReceiversItemMS) => {
        const idx = Number((props as any)["data-option-index"] ?? 0)
        const altClass = idx % 2 === 0 ? classes.altGrey : classes.altWhite

        return <BillReceiverOptionItem {...props} className={`${props.className ?? ""} ${altClass}`} option={option} />
    }

    const handleChange = (_e: React.SyntheticEvent, value: BillReceiversItemMS | null) => {
        helpers.setValue(value ?? null)
        if (isLoadOnOpen) {
            handleClearSearch()
        }
    }
    const handleCloseDialog = () => {
        setIsOpenCreateBill(false)
    }

    return (
        <>
            <ApiQueryAutocomplete
                isLoading={enabled && isLoading}
                isError={isError}
                options={options}
                getOptionLabel={(option: BillReceiversItemMS) => option.name || ""}
                value={field.value || null}
                onChange={handleChange}
                onInputChange={handleInputChange}
                onOpen={handleOpen}
                onClose={handleClose}
                size="small"
                slotProps={{
                    textField: formikErrorProps,
                    popper: {
                        placement: "bottom-end",
                        sx: {
                            width: "40rem !important",
                            "& .MuiAutocomplete-listbox": {
                                padding: 0,
                            },
                        },
                    },
                }}
                label={`${config?.label || config?.description}${config?.inputField?.isRequired ? " *" : ""}`}
                isOptionEqualToValue={(a: BillReceiversItemMS, b: BillReceiversItemMS) => a.id === b.id}
                filterOptions={(x) => x}
                readOnly={isEagerLoad && options.length === 1}
                renderOption={renderOption}
                ListboxComponent={showCreateButton ? ListboxWithHeader : undefined}
            />
            {isOpenCreateBill && (
                <CreateBillReceiver companyId={user.companyId} handleCloseDialog={handleCloseDialog} />
            )}
        </>
    )
}
